import { ref, watch, computed } from "vue";
import { storeToRefs } from "pinia";
import { Artefact } from "@/lib/artefacts";
import { api, types } from "@/lib/openapi";
import router from "@/router";
import { defineEntityStore } from "@/stores/helpers";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useSyncStore } from "@/stores/sync-store";
import {
    discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";
import { throttle } from "lodash";
import { syncChannel } from "@/lib/broadcast-channels";

export type Gateway = types.ProcessGateway;

// Uses the current route to determine what the 'gateway' should be and fetches
// the gateway description for you.
export const useGatewayStore = defineEntityStore("gateway", () => {
    const gateway = ref<Gateway>();
    const syncStore = useSyncStore();
    const entityId = computed(() => gateway.value?.identity);
    const description = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.GATEWAY,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.GATEWAY);

    const {
        organization,
        stek,
        process,
        gateway: gatewayInfo,
    } = useCurrentEntityManager();
    const isFetching = ref(false);
    const controller = ref(new AbortController());

    async function updateGateway(force = false) {
        const gatewayId = gatewayInfo.value?.identity;

        // In case the same gateway is selected, return early.
        if (!force && gateway.value && gateway.value.identity === gatewayId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!gatewayId) {
            description.value = undefined;
            gateway.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        gateway.value = undefined;

        const { response, data } =
            await api.startProcessProcessElementMgtGetGatewayStartPost({
                body: {
                    gateway_id: `${gatewayId}`,
                    include_archived: true,
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            if (response.status === 404) {
                router.replace({
                    name: "not-found",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                    query: {
                        type: "gateway",
                        id: gatewayId,
                    },
                });
                return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        gateway.value = data;
        isFetching.value = false;

        if (gateway.value) {
            await updateDescription(gatewayId);
        }
    }
    watch(
        () => gatewayInfo.value?.identity,
        () => updateGateway(),
        { immediate: true }
    );

    const updateDescription = async (currentGatewayId?: string | undefined) => {
        if (!gateway.value) {
            return;
        }

        if (!description.value) {
            try {
                const descriptionResult = await getDescriptionArtefact(
                    gateway.value,
                    "gatewayDescription",
                    controller.value.signal
                );

                // Check if gateway has changed during the async operation
                if (
                    !gateway.value ||
                    gateway.value.identity !== currentGatewayId
                ) {
                    console.debug(
                        "Gateway changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }

        return !!description.value?.isModified;
    });

    const queueSave = async () => {
        if (!isModified.value || !gateway.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateGatewayStartPostData =
            {
                body: {
                    gateway_id: gateway.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.gateway_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only gateway_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.GATEWAY,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.GATEWAY &&
                identity.entityId === gateway.value?.identity
            ) {
                updateGateway(true);
            }
        }
    );

    const deleteGateway = async () => {
        if (!gateway.value) {
            return;
        }
        if (!gateway.value.is_archived) {
            throw Error("Can only delete archived gateways");
        }

        await api.startProcessProcessElementMgtDeleteGatewayStartPost({
            body: {
                gateway_id: gateway.value.identity,
            },
        });

        if (organization.value && stek.value && process.value) {
            await router.push({
                name: "processPage",
                params: {
                    orgName: organization.value.name,
                    stekName: stek.value.name,
                    processId: process.value.identity,
                },
            });
        }
    };

    return {
        gateway,
        description,
        isModified,
        isFetching,
        reset,
        deleteGateway,
    };
});

export const useGatewayManager = () => storeToRefs(useGatewayStore());
